import { normalizeRoomName } from "@/lib/roomUtils";

export type RoomSpineState = "not_captured" | "captured" | "extras_left" | "extras_done";

export type RoomSpineRow = {
  id: string;
  name: string;
  itemCount: number;
  extrasLeft: number;
  state: RoomSpineState;
  meta: string;
};

export function roomSpineState(input: { itemCount: number; extrasLeft: number }): RoomSpineState {
  if (input.itemCount <= 0) return "not_captured";
  if (input.extrasLeft > 0) return "extras_left";
  return "extras_done";
}

export function roomSpineMeta(row: {
  itemCount: number;
  extrasLeft: number;
  state: RoomSpineState;
  onboardComplete: boolean;
}): string {
  if (row.state === "not_captured") return "Not captured";
  if (row.state === "extras_left") {
    const n = row.extrasLeft;
    return `Captured · ${n} extra${n === 1 ? "" : "s"} left`;
  }
  if (row.onboardComplete) {
    return `${row.itemCount} item${row.itemCount === 1 ? "" : "s"}`;
  }
  return `${row.itemCount} item${row.itemCount === 1 ? "" : "s"} · Extras done`;
}

export function extrasLeftByPropertyRoomId(
  propertyRooms: { id: string; name: string }[],
  invRooms: { id: string; name: string; property_room_id?: string | null }[],
  inventory: { room_id?: string | null; guest_guide_status?: string | null }[],
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const room of propertyRooms) map[room.id] = 0;

  const invRoomToProperty = new Map<string, string>();
  for (const invRoom of invRooms) {
    if (invRoom.property_room_id && map[invRoom.property_room_id] !== undefined) {
      invRoomToProperty.set(invRoom.id, invRoom.property_room_id);
      continue;
    }
    const n = normalizeRoomName(invRoom.name);
    const match = propertyRooms.find((room) => normalizeRoomName(room.name) === n);
    if (match) invRoomToProperty.set(invRoom.id, match.id);
  }

  for (const item of inventory) {
    const status = item.guest_guide_status || "pending";
    if (status !== "pending") continue;
    const propertyRoomId = invRoomToProperty.get(String(item.room_id || ""));
    if (!propertyRoomId) continue;
    map[propertyRoomId] += 1;
  }
  return map;
}

export function buildRoomSpineRows(input: {
  rooms: { id: string; name: string }[];
  itemCountByRoomId: Record<string, number>;
  extrasLeftByRoomId: Record<string, number>;
  onboardComplete: boolean;
}): RoomSpineRow[] {
  return input.rooms.map((room) => {
    const itemCount = input.itemCountByRoomId[room.id] ?? 0;
    const extrasLeft = input.extrasLeftByRoomId[room.id] ?? 0;
    const state = roomSpineState({ itemCount, extrasLeft });
    return {
      id: room.id,
      name: room.name,
      itemCount,
      extrasLeft,
      state,
      meta: roomSpineMeta({ itemCount, extrasLeft, state, onboardComplete: input.onboardComplete }),
    };
  });
}

export function roomsDoneCount(rows: RoomSpineRow[]): number {
  return rows.filter((row) => row.state === "extras_done").length;
}

export function onboardIsComplete(rows: RoomSpineRow[]): boolean {
  return rows.length > 0 && rows.every((row) => row.state === "extras_done");
}

/** Unfinished extras first, then first uncaptured room. */
export function primarySpineAction(rows: RoomSpineRow[]): {
  kind: "extras" | "capture";
  room: RoomSpineRow;
} | null {
  const extras = rows.find((row) => row.state === "extras_left");
  if (extras) return { kind: "extras", room: extras };
  const next = rows.find((row) => row.state === "not_captured");
  if (next) return { kind: "capture", room: next };
  return null;
}

export function nextUncapturedRoom(rows: RoomSpineRow[], afterName?: string | null): RoomSpineRow | null {
  const after = normalizeRoomName(afterName || "");
  const idx = after ? rows.findIndex((row) => normalizeRoomName(row.name) === after) : -1;
  const ordered = idx >= 0 ? [...rows.slice(idx + 1), ...rows.slice(0, idx)] : rows;
  return ordered.find((row) => row.state === "not_captured") ?? null;
}

export function extrasLeftForRoomName(
  extrasLeftByRoomId: Record<string, number>,
  rooms: { id: string; name: string }[],
  roomName: string,
): number {
  const n = normalizeRoomName(roomName);
  const room = rooms.find((row) => normalizeRoomName(row.name) === n);
  if (!room) return 0;
  return extrasLeftByRoomId[room.id] ?? 0;
}

export function primaryButtonLabel(action: { kind: "extras" | "capture"; room: RoomSpineRow }): string {
  if (action.kind === "extras") return `Continue ${action.room.name} extras`;
  return `Start in ${action.room.name}`;
}
